import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, ScrollText, LogOut } from "lucide-react";
import { Checkbox } from "./ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

// Server-authoritative Terms gate (V2, hardened 2026-10-02): a full-screen
// BLOCKING screen — no dismiss, no "later". Renders from GET /api/terms/status
// (never localStorage) and re-opens whenever any protected API call answers
// 403 TERMS_REQUIRED. Acceptance is recorded server-side with the current
// version; a version bump automatically re-opens the gate. Decline keeps the
// user signed in but every protected workflow stays locked server-side.
export default function TermsGate() {
  const { user, refresh, logout } = useAuth();
  const { pathname } = useLocation();
  // Legal pages stay readable — the user must be able to READ the document
  // they are being asked to accept. Every other screen is blocked.
  const onLegalPage = ["/terms", "/privacy", "/legal", "/accessibility"].some((p) => pathname.startsWith(p));
  const [terms, setTerms] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [declined, setDeclined] = useState(false);

  const uid = user?.user_id;
  const load = () => {
    if (!uid) { setTerms(null); return; }
    api.termsStatus().then((t) => setTerms(t)).catch(() => {});
  };
  useEffect(() => {
    setChecked(false);
    setError("");
    setDeclined(false);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Any protected API answering TERMS_REQUIRED re-opens the gate immediately.
  useEffect(() => {
    const onRequired = () => { setDeclined(false); load(); };
    window.addEventListener("mrp:terms-required", onRequired);
    return () => window.removeEventListener("mrp:terms-required", onRequired);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  if (!terms?.required || onLegalPage) return null;

  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      const t = await api.termsAccept("web");
      setTerms(t);
      await refresh();
      // Pages behind the gate already failed their protected fetches with 403;
      // a reload re-runs them against the now-accepted session.
      window.location.reload();
    } catch (e) {
      setError(e.message || "Could not record acceptance — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#080B0D]/95 backdrop-blur-md"
         role="dialog" aria-modal="true" aria-label="Terms of Service acceptance required"
         data-testid="terms-gate">
      <div className="w-full max-w-lg card-dark border-[#27272A] p-6 sm:p-8 shadow-2xl shadow-black/60">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-[#E50914]/15 flex items-center justify-center shrink-0">
            <ScrollText className="w-5 h-5 text-[#FF3B45]" />
          </span>
          <div>
            <p className="font-display text-xl text-white font-bold">Terms of Service</p>
            <p className="text-xs text-zinc-500">Version {terms.current_version} · acceptance required</p>
          </div>
        </div>

        {declined ? (
          <div className="mt-5" data-testid="terms-gate-declined">
            <p className="text-sm text-zinc-400 leading-relaxed">
              You declined the current Terms of Service. Your account stays signed in, but booking,
              hosting, payments, wallet, payouts and every other protected workflow remain locked
              until you accept the current version.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setDeclined(false)} data-testid="terms-gate-review"
                      className="btn-primary flex-1 text-sm">Review &amp; accept</button>
              <button type="button" onClick={logout} data-testid="terms-gate-signout"
                      className="btn-secondary flex-1 text-sm"><LogOut className="w-4 h-4" /> Sign out</button>
            </div>
          </div>
        ) : (
          <div className="mt-5">
            <p className="text-sm text-zinc-400 leading-relaxed">
              Please review and accept the current MyRedPark Terms of Service
              (v{terms.current_version}) to continue. This applies to every account — driver, host,
              commercial and admin.
            </p>
            <label className="mt-5 flex items-start gap-3 text-sm text-zinc-300 cursor-pointer select-none">
              <Checkbox checked={checked} onCheckedChange={(v) => setChecked(v === true)}
                        data-testid="terms-gate-checkbox" className="mt-0.5" />
              <span>
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#FF3B45] underline underline-offset-2">
                  Terms of Service
                </Link>{" "}
                (version {terms.current_version}).
              </span>
            </label>
            {error && <p role="alert" data-testid="terms-gate-error" className="mt-3 text-sm text-[#FF6B6B]">{error}</p>}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setDeclined(true)} data-testid="terms-gate-decline"
                      className="btn-secondary flex-1 text-sm">Decline</button>
              <button type="button" onClick={accept} disabled={!checked || busy} data-testid="terms-gate-accept"
                      className="btn-primary flex-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <ShieldCheck className="w-4 h-4" /> {busy ? "Saving…" : "Accept & continue"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
